import { ReactNode } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import UI from '../../common/UI';

import { Permission } from '../../__generated__/graphql';
import ProtectedSection from '../Common/ProtectedSection';
import useProject from '../useProject';

enum Route {
  Registrations = 'registrations',
  Contacts = 'contacts',
  Teams = 'teams',
  Orders = 'orders',
  TicketNumbers = 'bib-numbers',
  Scanners = 'scanners',
  Lotteries = 'lotteries',
  Imports = 'imports',
}

const pathToRoute = (path: string | null): Route => {
  if (!path) {
    return Route.Registrations;
  }

  return Object.values(Route).includes(path as Route)
    ? path as Route
    : Route.Contacts;
};

export interface ParticipantsLayoutProps {
  children: ReactNode;
}

const ParticipantsLayout = ({ children }: ParticipantsLayoutProps) => {
  const { t } = useTranslation();
  const project = useProject();

  const match = useRouteMatch<{ path?: string; subPath?: string; }>(
    '/:projectId/participants/:path?/:subPath?',
  );

  const path = match?.params.path;

  const route = pathToRoute(path);

  return (
    <>
      <UI.Hero>
        <UI.Container sc={{ pt: 0.5, px: [0, 4], textAlign: [undefined, 'center'] }}>
          <UI.Tabs>
            <ProtectedSection permissions={[Permission.view_registrations]}>
              <UI.Tab
                to={`/${project.id}/participants`}
                className={route === Route.Registrations && 'active'}
                exact
              >
                {t('registrations')}
              </UI.Tab>
              <UI.Tab
                to={`/${project.id}/participants/contacts`}
                className={route === Route.Contacts && 'active'}
                exact
              >
                {t('contacts')}
              </UI.Tab>
              <UI.Tab
                to={`/${project.id}/participants/orders`}
                className={route === Route.Orders && 'active'}
                exact
              >
                {t('orders')}
              </UI.Tab>
              <UI.Tab
                to={`/${project.id}/participants/teams`}
                className={route === Route.Teams && 'active'}
                exact
              >
                {t('teams')}
              </UI.Tab>
            </ProtectedSection>
            <ProtectedSection permissions={[Permission.participant_logistics]}>
              <UI.Tab
                to={`/${project.id}/participants/bib-numbers`}
                className={route === Route.TicketNumbers && 'active'}
                exact
              >
                {t('ticket_numbers')}
              </UI.Tab>
              <UI.Tab
                to={`/${project.id}/participants/scanners`}
                className={route === Route.Scanners && 'active'}
                exact
              >
                {t('scanners')}
              </UI.Tab>
              <UI.Tab
                to={`/${project.id}/participants/lotteries`}
                className={route === Route.Lotteries && 'active'}
                exact
              >
                {t('lotteries')}
              </UI.Tab>
            </ProtectedSection>
            <ProtectedSection permissions={[Permission.edit_registrations]}>
              <UI.Tab
                to={`/${project.id}/participants/imports`}
                className={route === Route.Imports && 'active'}
                exact
              >
                {t('imports')}
              </UI.Tab>
            </ProtectedSection>
          </UI.Tabs>
        </UI.Container>
      </UI.Hero>

      <UI.Div id="ParticipantsTab">
        {children}
      </UI.Div>
    </>
  );
};

export default ParticipantsLayout;
